import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, Sparkles, Loader2, RefreshCw, CheckCircle2, XCircle,
  AlertCircle, ExternalLink, Brain, Zap, Users, TrendingUp, Crown,
} from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip,
  CartesianGrid, Legend, Line, ComposedChart,
} from "recharts";
import { adminApi } from "../api";
import { useAdminAuth } from "../contexts/AdminAuthContext";

function KpiCard({ label, value, suffix = "", accent = "default", testid }) {
  const cls = {
    default: "bg-white border-[#F3F0E7] text-[#1B1A17]",
    ok: "bg-emerald-50 border-emerald-200 text-emerald-900",
    warn: "bg-amber-50 border-amber-200 text-amber-900",
    hot: "bg-red-50 border-red-200 text-red-900",
    info: "bg-blue-50 border-blue-200 text-blue-900",
  }[accent];
  return (
    <div data-testid={testid} className={`p-4 rounded-lg border ${cls}`}>
      <p className="text-[10px] uppercase tracking-[0.15em] font-semibold opacity-70">{label}</p>
      <p className="text-2xl font-bold font-mono mt-1">
        {value}
        {suffix && <span className="text-sm font-normal opacity-70 ml-1">{suffix}</span>}
      </p>
    </div>
  );
}

function StatusPill({ ok, label, testid }) {
  return (
    <span data-testid={testid} className={`inline-flex items-center gap-1.5 text-xs font-medium px-2 py-1 rounded ${
      ok ? "bg-emerald-100 text-emerald-800" : "bg-red-100 text-red-800"
    }`}>
      {ok ? <CheckCircle2 size={12} /> : <XCircle size={12} />}
      {label}
    </span>
  );
}

function priorityBadgeCls(level) {
  return level === "high" ? "bg-red-100 text-red-700"
    : level === "medium" ? "bg-amber-100 text-amber-700"
    : level === "low" ? "bg-emerald-100 text-emerald-700"
    : "bg-gray-100 text-gray-600";
}

function statusBadgeCls(status) {
  return status === "completed" ? "bg-emerald-100 text-emerald-700"
    : status === "partial" ? "bg-amber-100 text-amber-700"
    : status === "failed" ? "bg-red-100 text-red-700"
    : status === "processing" ? "bg-blue-100 text-blue-700"
    : "bg-gray-100 text-gray-600";
}

export default function AdminLeadIntelligence() {
  const { user } = useAdminAuth();
  const isSolfut = Number(user?.ID_Cliente) === 1;
  const [config, setConfig] = useState(null);
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);
  const [timeseries, setTimeseries] = useState(null);
  const [rangeDays, setRangeDays] = useState(30);
  const [loading, setLoading] = useState(true);
  const [reprocessing, setReprocessing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const promises = [
        adminApi.leadIntelConfig(),
        adminApi.leadIntelStats(),
        adminApi.leadIntelRecent(50),
      ];
      if (isSolfut) promises.push(adminApi.leadIntelTimeseries(rangeDays));
      const results = await Promise.all(promises);
      setConfig(results[0].data || results[0]);
      setStats(results[1].data || results[1]);
      setRecent(results[2].data || results[2] || []);
      if (isSolfut) setTimeseries(results[3].data || results[3]);
    } catch (e) {
      toast.error(e.message || "Erro ao carregar dados");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [rangeDays, isSolfut]);

  const doReprocess = async (id) => {
    setReprocessing(id);
    try {
      const res = await adminApi.leadIntelReprocess(id);
      const status = res.data?.status || res.status;
      if (status === "completed") toast.success(`Lead #${id} reanalisada`);
      else if (status === "cached") toast.info(`Lead #${id} recente — cache mantido`);
      else if (status === "partial") toast.warning(`Lead #${id} parcial (IA falhou)`);
      else toast.error(`Lead #${id} falhou: ${res.data?.error || status}`);
      await load();
    } catch (e) {
      toast.error(e.message || "Erro ao reprocessar");
    } finally {
      setReprocessing(null);
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center gap-2 text-[#6B6459]">
        <Loader2 className="animate-spin" size={18} /> A carregar Inteligência da Lead…
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="admin-lead-intelligence-page" style={{ fontFamily: "'Manrope', sans-serif" }}>
      <Link to="/admin" className="inline-flex items-center gap-1 text-sm text-[#6B6459] hover:text-[#1B1A17] mb-4">
        <ArrowLeft size={14} /> Voltar ao Admin
      </Link>

      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-full bg-gradient-to-br from-[#1B1A17] to-[#6366F1] flex items-center justify-center">
            <Sparkles size={22} className="text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-[#1B1A17]" style={{ fontFamily: "'Fraunces', sans-serif" }}>
              Inteligência da Lead
            </h1>
            <p className="text-sm text-[#6B6459]">Pipeline IA que enriquece e avalia cada lead nova automaticamente.</p>
          </div>
        </div>
        <button data-testid="lead-intel-refresh-btn" onClick={load}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border border-[#E5E1D6] rounded-md hover:bg-[#F3F0E7]">
          <RefreshCw size={14} /> Actualizar
        </button>
      </div>

      {/* Config */}
      <section className="mb-6">
        <h2 className="text-[11px] uppercase tracking-[0.18em] text-[#978D78] font-bold mb-2">Configuração</h2>
        <div className="bg-white border border-[#F3F0E7] rounded-lg p-4">
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <StatusPill ok={!!config?.auto_enabled} label={config?.auto_enabled ? "Auto-trigger activo" : "Auto-trigger desligado"} testid="pill-auto-enabled" />
            <StatusPill ok={!!config?.llm?.configured} label={`LLM ${config?.llm?.configured ? "configurado" : "sem chave"}`} testid="pill-llm" />
            <StatusPill ok={!!config?.email_validation?.configured} label="Validação Email" testid="pill-email-val" />
            <StatusPill ok={!!config?.internal_crm?.configured} label="CRM interno" testid="pill-crm" />
          </div>
          <dl className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
            <div><dt className="text-[#978D78] uppercase tracking-wider">Provider LLM</dt><dd className="font-mono mt-0.5">{config?.llm?.provider || "—"}</dd></div>
            <div><dt className="text-[#978D78] uppercase tracking-wider">Modelo</dt><dd className="font-mono mt-0.5 truncate" title={config?.llm?.model}>{config?.llm?.model || "—"}</dd></div>
            <div><dt className="text-[#978D78] uppercase tracking-wider">Company Enrichment</dt><dd className="mt-0.5">{config?.company_enrichment?.mode || "—"}</dd></div>
            <div><dt className="text-[#978D78] uppercase tracking-wider">Schema</dt><dd className="font-mono mt-0.5">v{config?.schema_version}</dd></div>
          </dl>
          <p className="text-[11px] text-[#978D78] mt-3">
            Para desligar o auto-trigger sem código, defina <code className="font-mono">LEAD_INTEL_ENABLE_AUTO=0</code> no
            ficheiro <code className="font-mono">/app/backend/.env</code> e reinicie o backend.
          </p>
        </div>
      </section>

      {/* Evolução — só para Solfut (ID_Cliente=1) */}
      {isSolfut && (
        <section className="mb-6" data-testid="solfut-timeseries-section">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-[11px] uppercase tracking-[0.18em] text-[#978D78] font-bold flex items-center gap-1.5">
              <Crown size={13} className="text-amber-500" /> Evolução (exclusivo Solfut)
            </h2>
            <div className="flex items-center gap-1 bg-white border border-[#F3F0E7] rounded p-0.5">
              {[7, 30, 90].map((d) => (
                <button key={d} data-testid={`range-${d}d`}
                  onClick={() => setRangeDays(d)}
                  className={`px-2.5 py-1 text-[11px] font-medium rounded transition-colors ${
                    rangeDays === d ? "bg-[#1B1A17] text-white" : "text-[#6B6459] hover:bg-[#F3F0E7]"
                  }`}>{d}d</button>
              ))}
            </div>
          </div>
          <div className="bg-white border border-[#F3F0E7] rounded-lg p-4">
            {!timeseries || !timeseries.series?.length ? (
              <div className="text-center py-16 text-sm text-[#978D78]">Sem dados no período seleccionado.</div>
            ) : (
              <>
                <div className="flex flex-wrap gap-4 mb-3 text-xs">
                  <span data-testid="ts-total"><span className="text-[#978D78]">Total:</span> <span className="font-mono font-semibold">{timeseries.totals.total}</span></span>
                  <span><span className="text-red-600">■</span> Alta: <span className="font-mono font-semibold">{timeseries.totals.high}</span></span>
                  <span><span className="text-amber-500">■</span> Média: <span className="font-mono font-semibold">{timeseries.totals.medium}</span></span>
                  <span><span className="text-emerald-600">■</span> Baixa: <span className="font-mono font-semibold">{timeseries.totals.low}</span></span>
                  <span><span className="text-gray-500">■</span> Falhadas: <span className="font-mono font-semibold">{timeseries.totals.failed}</span></span>
                </div>
                <div style={{ width: "100%", height: 280 }}>
                  <ResponsiveContainer>
                    <ComposedChart data={timeseries.series} margin={{ top: 10, right: 20, bottom: 0, left: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#F3F0E7" />
                      <XAxis dataKey="day" tick={{ fontSize: 10, fill: "#6B6459" }}
                        tickFormatter={(d) => (d || "").slice(5)} interval="preserveStartEnd" />
                      <YAxis yAxisId="left" tick={{ fontSize: 10, fill: "#6B6459" }} />
                      <YAxis yAxisId="right" orientation="right" domain={[0, 100]}
                        tick={{ fontSize: 10, fill: "#6366F1" }} />
                      <Tooltip contentStyle={{ fontSize: 12, borderRadius: 6 }} />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                      <Bar yAxisId="left" dataKey="high" stackId="p" fill="#DC2626" name="Alta" />
                      <Bar yAxisId="left" dataKey="medium" stackId="p" fill="#F59E0B" name="Média" />
                      <Bar yAxisId="left" dataKey="low" stackId="p" fill="#059669" name="Baixa" />
                      <Bar yAxisId="left" dataKey="failed" stackId="p" fill="#9CA3AF" name="Falhadas" />
                      <Line yAxisId="right" type="monotone" dataKey="avgScore" stroke="#6366F1"
                        strokeWidth={2} dot={{ r: 3 }} name="Score médio" />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
              </>
            )}
          </div>
        </section>
      )}

      {/* KPIs */}
      <section className="mb-6">
        <h2 className="text-[11px] uppercase tracking-[0.18em] text-[#978D78] font-bold mb-2">Estatísticas</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <KpiCard label="Total processadas" value={stats?.total_enrichments || 0} accent="info" testid="kpi-total" />
          <KpiCard label="Concluídas" value={stats?.completed || 0} accent="ok" testid="kpi-completed" />
          <KpiCard label="Parciais" value={stats?.partial || 0} accent="warn" testid="kpi-partial" />
          <KpiCard label="Falhadas" value={stats?.failed || 0} accent="hot" testid="kpi-failed" />
          <KpiCard label="Prioridade Alta" value={stats?.high_priority || 0} accent="hot" testid="kpi-high" />
          <KpiCard label="Prioridade Média" value={stats?.medium_priority || 0} accent="warn" testid="kpi-medium" />
          <KpiCard label="Prioridade Baixa" value={stats?.low_priority || 0} accent="ok" testid="kpi-low" />
          <KpiCard label="Confiança média" value={stats?.avg_confidence || 0} suffix="%" accent="info" testid="kpi-confidence" />
          <KpiCard label="Score Prioridade médio" value={stats?.avg_priority_score || 0} accent="default" testid="kpi-avg-priority" />
          <KpiCard label="Score Intenção médio" value={stats?.avg_intent_score || 0} accent="default" testid="kpi-avg-intent" />
          <KpiCard label="Score Afinidade médio" value={stats?.avg_affinity_score || 0} accent="default" testid="kpi-avg-affinity" />
          <KpiCard label="Assessments" value={stats?.assessments_total || 0} accent="info" testid="kpi-assessments" />
        </div>
      </section>

      {/* Recent */}
      <section>
        <h2 className="text-[11px] uppercase tracking-[0.18em] text-[#978D78] font-bold mb-2">Análises Recentes</h2>
        <div className="bg-white border border-[#F3F0E7] rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="admin-lead-intel-table">
              <thead className="bg-[#FAF7F0]">
                <tr>
                  <th className="text-left text-[10px] uppercase tracking-[0.15em] font-medium text-[#6B6459] px-3 py-2">Lead</th>
                  <th className="text-left text-[10px] uppercase tracking-[0.15em] font-medium text-[#6B6459] px-3 py-2">Estado</th>
                  <th className="text-left text-[10px] uppercase tracking-[0.15em] font-medium text-[#6B6459] px-3 py-2">Prioridade</th>
                  <th className="text-right text-[10px] uppercase tracking-[0.15em] font-medium text-[#6B6459] px-3 py-2">Score</th>
                  <th className="text-left text-[10px] uppercase tracking-[0.15em] font-medium text-[#6B6459] px-3 py-2">Resumo</th>
                  <th className="text-left text-[10px] uppercase tracking-[0.15em] font-medium text-[#6B6459] px-3 py-2">Data</th>
                  <th className="text-right text-[10px] uppercase tracking-[0.15em] font-medium text-[#6B6459] px-3 py-2 w-[100px]">Acções</th>
                </tr>
              </thead>
              <tbody>
                {recent.length === 0 && (
                  <tr><td colSpan={7} className="text-center py-10 text-sm text-[#978D78]">Ainda não há leads analisadas.</td></tr>
                )}
                {recent.map((r) => (
                  <tr key={r.id_negocio} className="border-t border-[#F3F0E7] hover:bg-[#FAF7F0]" data-testid={`row-lead-${r.id_negocio}`}>
                    <td className="px-3 py-2 text-sm">
                      <a href={`/negocios/${r.id_negocio}`} target="_blank" rel="noreferrer"
                        className="font-mono text-[#1B1A17] hover:underline inline-flex items-center gap-1">
                        #{r.id_negocio} <ExternalLink size={11} />
                      </a>
                      {r.isExistingClient && <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 font-medium">Cliente</span>}
                      {r.emailType === "corporate" && <span className="ml-1 text-[10px] px-1.5 py-0.5 rounded bg-blue-100 text-blue-800 font-medium">Corp</span>}
                    </td>
                    <td className="px-3 py-2 text-sm">
                      <span className={`text-[11px] font-medium px-2 py-0.5 rounded ${statusBadgeCls(r.status)}`}>{r.status || "—"}</span>
                    </td>
                    <td className="px-3 py-2 text-sm">
                      {r.priorityLevel ? (
                        <span className={`text-[11px] font-medium px-2 py-0.5 rounded ${priorityBadgeCls(r.priorityLevel)}`}>{r.priorityLevel}</span>
                      ) : <span className="text-[#978D78]">—</span>}
                    </td>
                    <td className="px-3 py-2 text-sm font-mono text-right">{r.priorityScore ?? "—"}</td>
                    <td className="px-3 py-2 text-xs text-[#4B4741] max-w-[380px] truncate" title={r.executiveSummary}>
                      {r.executiveSummary || <span className="italic text-[#978D78]">sem resumo</span>}
                    </td>
                    <td className="px-3 py-2 text-xs text-[#6B6459] whitespace-nowrap">
                      {(r.completedAt || r.startedAt || "").replace("T", " ").slice(0, 16)}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <button data-testid={`reprocess-btn-${r.id_negocio}`}
                        onClick={() => doReprocess(r.id_negocio)}
                        disabled={reprocessing === r.id_negocio}
                        className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-medium border border-[#E5E1D6] rounded hover:bg-[#F3F0E7] disabled:opacity-50">
                        {reprocessing === r.id_negocio ? <Loader2 size={11} className="animate-spin" /> : <Zap size={11} />}
                        Actualizar
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>
    </div>
  );
}
